import {
  IEntity,
  IEntityType,
  Entity,
  EntityFields,
  EntityAttributes,
  EntityAssociations,
} from 'app/types/entity';

export function hasAttributeWithType<T extends IEntity, U extends IEntity | 'string' | 'number'>(
  entity: Entity<T>,
  attributeName: keyof IEntityType<T>['attributes'],
  attributeType: U,
) {
  const attribute = entity.schema.attributes[attributeName];

  return attribute && attribute.type === attributeType;
}

export function getEntityTypeName<T extends IEntity>(entity: Entity<T>) {
  return entity.schema.typeName;
}

export const getFieldSchema = <T extends IEntity>(
  entity: Entity<T>,
  name: keyof EntityFields<T>,
) => entity.schema.associations[name] || entity.schema.attributes[name];

export const getFieldType = <T extends IEntity>(
  entity: Entity<T>,
  name: keyof EntityFields<T>,
) => {
  const attrSchema = getFieldSchema(entity, name);
  return attrSchema ? attrSchema.type : null;
};

export const notNull = <T extends IEntity>(
  entity: Entity<T>,
  name: keyof EntityFields<T>,
) => {
  const attrSchema = getFieldSchema(entity, name);
  return attrSchema ? attrSchema.notNull : false;
};

export const isCollection = <T extends IEntity>(
  entity: Entity<T>,
  name: keyof EntityFields<T>,
) => {
  const attrSchema = getFieldSchema(entity, name);
  return attrSchema ? attrSchema.collection : false;
};

// Discriminant for Descriptor interfaces.
export enum DescriptorType {
  Attribute = 'Attribute',
  Association = 'Association',
}

interface IDescriptorBase<Field> {
  field: Field;
  notNull: boolean;
  collection: boolean;
  descriptorType: DescriptorType;
}

interface IAttributeDescriptor<T extends IEntity, K extends keyof EntityAttributes<T>> extends IDescriptorBase<K> {
  type: IEntityType<T>['attributes'][K]['type'];
  descriptorType: DescriptorType.Attribute;
}

interface IAssociationDescriptor<T extends IEntity, K extends keyof EntityAssociations<T>> extends IDescriptorBase<K> {
  type: IEntityType<T>['associations'][K]['type'],
  shared: boolean;
  descriptorType: DescriptorType.Association;
}

type Descriptor<T extends IEntity> =
  IAttributeDescriptor<T, keyof EntityAttributes<T>> |
  IAssociationDescriptor<T, keyof EntityAssociations<T>>;

type OnAttributeCallbackType<T extends IEntity, A> =
  (attr: IAttributeDescriptor<T, keyof EntityAttributes<T>>) => A;
type OnAssociationCallbackType<T extends IEntity, A> =
  (assoc: IAssociationDescriptor<T, keyof EntityAssociations<T>>) => A;

export const walkSchemaAttributes = <T extends IEntity, A>(
  schema: IEntityType<T>,
  func: OnAttributeCallbackType<T, A>,
): {[K in keyof EntityAttributes<T>]: A} => {
  return Object.fromEntries(Object.entries(schema.attributes).map((keySpecPair: any): any => {
    const [key, spec] = keySpecPair;
    return [
      key,
      func({
        field: key,
        type: spec.type,
        collection: spec.collection,
        notNull: spec.notNull,
        descriptorType: DescriptorType.Attribute,
      },
    )];
  }));
};

export const walkSchemaAssociations = <T extends IEntity, A>(
  schema: IEntityType<T>,
  func: OnAssociationCallbackType<T, A>,
): {[K in keyof EntityAssociations<T>]: A} => {
  return Object.fromEntries(Object.entries(schema.associations).map((keySpecPair: any): any => {
    const [key, spec] = keySpecPair;
    return [
      key,
      func({
        field: key,
        type: spec.type,
        collection: spec.collection,
        notNull: spec.notNull,
        shared: spec.shared,
        descriptorType: DescriptorType.Association,
      },
    )];
  }));
};

interface IWalkSchemaArgs<T extends IEntity, A> {
  schema: IEntityType<T>,
  onAttribute: OnAttributeCallbackType<T, A>,
  onAssociation: OnAssociationCallbackType<T, A>,
}

export const walkSchemaAttributesAndAssociations = <T extends IEntity, A>({
  schema,
  onAttribute,
  onAssociation,
}: IWalkSchemaArgs<T, A>): {[k in keyof EntityFields<T>]: A} => {
  return { ...walkSchemaAttributes(schema, onAttribute), ...walkSchemaAssociations(schema, onAssociation) };
};

export type AnyFieldDescriptor<T extends IEntity> = IAttributeDescriptor<T, keyof EntityAttributes<T>> |
  IAssociationDescriptor<T, keyof EntityAssociations<T>>;

export const getEntityFields = <T extends IEntity>(entity: Entity<T>): EntityFields<T> => ({
  ...walkSchema({
    schema: entity.schema,
    onField: ({ field }) => entity[field],
  }) as unknown as EntityFields<T>,
});

export const walkSchema = <T extends IEntity, A>({
  schema,
  onField,
}: {
  schema: IEntityType<T>;
  onField: (descriptor: Descriptor<T>) => A;
}): {[K in keyof EntityFields<T>]: A} => {
  return walkSchemaAttributesAndAssociations({ schema, onAttribute: onField, onAssociation: onField });
};
